import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

// Howyin palette
const INK = "#1A1614";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const HAIR = "#E8E1D3";
const MUTED = "#8A857F";

const input = { padding: "9px 12px", borderRadius: 10, border: `1px solid ${HAIR}`, fontSize: 13, background: CREAM };
const btn = { padding: "9px 16px", borderRadius: 10, border: "none", background: GOLD, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" };
const th = { textAlign: "left", padding: "10px 12px", fontSize: 12, color: MUTED, fontWeight: 600, borderBottom: `1px solid ${HAIR}`, whiteSpace: "nowrap" };
const td = { padding: "10px 12px", fontSize: 13, color: INK, borderBottom: `1px solid ${HAIR}`, verticalAlign: "top" };

const Challenges = () => {
  const [challenges, setChallenges] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);

  // create form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [rewardCoins, setRewardCoins] = useState("");
  const [communityId, setCommunityId] = useState("");
  const [endsAt, setEndsAt] = useState("");

  // entries drawer
  const [entriesFor, setEntriesFor] = useState(null); // challenge object
  const [entries, setEntries] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiInstanceFetch.get("community/challenge/list");
      if (res && res.status) setChallenges(res.challenges || []);
      const cs = await apiInstanceFetch.get("community/admin/all?start=0&limit=100");
      if (cs && cs.status) setCommunities(cs.communities || cs.data || []);
    } catch (e) {
      console.error("challenge load", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createChallenge = async () => {
    if (!title || !rewardCoins || !communityId || !endsAt) return alert("Title, prize, community and end date are required");
    const res = await apiInstanceFetch.post("community/challenge/create", {
      title, description, rewardCoins: parseInt(rewardCoins, 10), linkedCommunityId: communityId, endsAt,
    });
    if (res && res.status) {
      setTitle(""); setDescription(""); setRewardCoins(""); setCommunityId(""); setEndsAt("");
      load();
    } else alert((res && res.message) || "Failed");
  };

  const endChallenge = async (id) => {
    if (!window.confirm("End this challenge?")) return;
    await apiInstanceFetch.patch("community/challenge/end", { challengeId: id });
    load();
  };

  const openEntries = async (ch) => {
    setEntriesFor(ch);
    setEntries([]);
    const cid = ch.linkedCommunityId && (ch.linkedCommunityId._id || ch.linkedCommunityId);
    const res = await apiInstanceFetch.get(`community/admin/posts?communityId=${cid}&start=0&limit=100`);
    if (res && res.status) setEntries(res.posts || []);
  };

  const award = async (post) => {
    const uid = post.userId && (post.userId._id || post.userId);
    const name = (post.userId && post.userId.firstName) || "this user";
    if (!window.confirm(`Award ${entriesFor.rewardCoins} coins to ${name}?`)) return;
    const res = await apiInstanceFetch.post("community/challenge/award", {
      challengeId: entriesFor._id, userId: uid, postId: post._id,
    });
    if (res && res.status) { alert("Awarded 🎉"); load(); setEntriesFor(null); }
    else alert((res && res.message) || "Failed");
  };

  return (
    <div style={{ padding: 16 }}>
      <Title name="Community Challenges" />

      {/* Create row */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "14px 0", background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, padding: 14 }}>
        <input style={{ ...input, flex: "2 1 200px" }} placeholder="Challenge title (e.g. Recipe video challenge)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input style={{ ...input, flex: "2 1 220px" }} placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        <input style={{ ...input, width: 120 }} type="number" placeholder="Prize coins" value={rewardCoins} onChange={(e) => setRewardCoins(e.target.value)} />
        <select style={{ ...input, width: 200 }} value={communityId} onChange={(e) => setCommunityId(e.target.value)}>
          <option value="">Entries community…</option>
          {communities.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <input style={{ ...input, width: 170 }} type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        <button style={btn} onClick={createChallenge}>Create</button>
      </div>

      {/* List */}
      <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
          <thead>
            <tr>
              <th style={th}>Challenge</th>
              <th style={th}>Community</th>
              <th style={th}>Prize</th>
              <th style={th}>Ends</th>
              <th style={th}>Status</th>
              <th style={th}>Winners</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={7}>Loading…</td></tr>
            ) : challenges.length === 0 ? (
              <tr><td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={7}>No challenges yet — create the first one above.</td></tr>
            ) : (
              challenges.map((c) => (
                <tr key={c._id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{c.title}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>{c.description}</div>
                  </td>
                  <td style={td}>{(c.linkedCommunityId && c.linkedCommunityId.name) || "-"}</td>
                  <td style={{ ...td, fontWeight: 700, color: GOLD }}>🏆 {c.rewardCoins}</td>
                  <td style={td}>{dayjs(c.endsAt).format("DD MMM YYYY")}</td>
                  <td style={td}>
                    <span style={{ background: c.status === "active" ? "#E8F6EC" : "#F1EEE7", color: c.status === "active" ? "#15803D" : MUTED, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10 }}>
                      {c.status}
                    </span>
                  </td>
                  <td style={td}>
                    {(c.winners || []).length === 0 ? "-" : (c.winners || []).map((w, i) => (
                      <div key={i} style={{ fontSize: 12 }}>🏅 {(w.userId && w.userId.firstName) || "user"} · {w.coins}</div>
                    ))}
                  </td>
                  <td style={td}>
                    <button style={{ ...btn, padding: "6px 12px", marginRight: 6 }} onClick={() => openEntries(c)}>Entries</button>
                    {c.status === "active" && (
                      <button style={{ ...btn, background: "#FDEAEA", color: "#DC2626", padding: "6px 12px" }} onClick={() => endChallenge(c._id)}>End</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Entries drawer */}
      {entriesFor && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(26,22,20,0.5)", zIndex: 60, display: "flex", justifyContent: "flex-end" }} onClick={() => setEntriesFor(null)}>
          <div style={{ width: 460, maxWidth: "92%", background: "#fff", height: "100%", overflow: "auto", padding: 18 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "4px 0 2px", color: INK }}>Entries — {entriesFor.title}</h3>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: MUTED }}>
              Posts in "{entriesFor.linkedCommunityId && entriesFor.linkedCommunityId.name}". Award pays 🏆 {entriesFor.rewardCoins} coins instantly.
            </p>
            {entries.length === 0 ? (
              <p style={{ color: MUTED, fontSize: 13 }}>No posts yet.</p>
            ) : (
              entries.map((p) => (
                <div key={p._id} style={{ border: `1px solid ${HAIR}`, borderRadius: 12, padding: 12, marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{(p.userId && `${p.userId.firstName || ""} ${p.userId.lastName || ""}`) || "user"}</div>
                  <div style={{ fontSize: 12.5, color: "#3A342E", margin: "4px 0" }}>{(p.text || "").slice(0, 220)}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: MUTED }}>❤️ {p.likesCount || 0} · 💬 {p.commentsCount || 0} · {dayjs(p.createdAt).format("DD MMM")}</span>
                    <button style={{ ...btn, padding: "6px 12px" }} onClick={() => award(p)}>Award 🏆</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Challenges;
